"use client";

import { useContext, useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import Cookies from "js-cookie";
import { AuthContext } from "contexts/AuthContext";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useJsApiLoader } from '@react-google-maps/api';
import api from "utils/__api__/address";

export default function LocationModal({ show = false, delay = 500 }) {
    const [open, setOpen] = useState(false);
    const { auth } = useContext(AuthContext);
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [addressInput, setAddressInput] = useState("");
    const [searching, setSearching] = useState(false);
    const [nodata, setNodata] = useState(false);
    const [addressList, setAddressList] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState("");

    const handleSelect = (address, latitude, longitude) => {
        setSelectedAddress(address);
        Cookies.remove("user_coords");
        Cookies.set("user_coords", `${latitude},${longitude}`);
        Cookies.set("address", address);
        window.location.reload();
    };

    const fetchAddresses = async () => {
        try {
            const data = await api.getAddressList(1);
            setAddressList(data.addressList.data);
            if (data.addressList.data.length > 0 && selectedAddress === '') {
                setSelectedAddress(data.addressList.data[0].address);
            }
        } catch (err) {
            console.error("Error loading addresses", err);
        }
    };

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY,
    });

    const handleSearch = (search) => {
        if (!search.trim() || !isLoaded) return;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: search }, (results, status) => {
            if (status === 'OK' && results.length) {
                setAddressSuggestions(results);
            } else {
                setAddressSuggestions([]);
                setNodata(true);
                setTimeout(() => setNodata(false), 3000);
            }
            setSearching(false);
        });
    };

    useEffect(() => {
        if (auth.isAuthenticated && open) fetchAddresses();
        const delayDebounceFn = setTimeout(() => {
            if (addressInput.trim()) {
                setSearching(true);
                handleSearch(addressInput);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [addressInput]);

    useEffect(() => {
        if (auth.isAuthenticated) fetchAddresses();
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const coords = Cookies.get("user_coords");
        if (!coords || show) {
            setTimeout(() => setOpen(true), delay);
        }
    }, []);

    const currentAddress = Cookies.get("address") || "";

    return (
        <Modal
            open={open}
            aria-labelledby="Location Modal"
            sx={{
                display: "flex",
                alignItems: { xs: "flex-end", sm: "center" },
                justifyContent: "center",
                zIndex: 999999999,
            }}
        >
            <Box
                sx={{
                    bgcolor: "background.paper",
                    borderRadius: { xs: "20px 20px 0 0", sm: 3 },
                    boxShadow: 24,
                    width: { xs: "100%", sm: "90%" },
                    maxWidth: { sm: 480 },
                    maxHeight: { xs: "85vh", sm: "90vh" },
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        px: 3,
                        pt: { xs: 2, sm: 3 },
                        pb: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    {/* Drag handle for mobile */}
                    <Box
                        sx={{
                            display: { xs: "block", sm: "none" },
                            width: 40,
                            height: 4,
                            bgcolor: "grey.300",
                            borderRadius: 2,
                            mb: 1,
                        }}
                    />
                    <Box
                        component="img"
                        src="/assets/images/geolocation.png"
                        alt="Ubicación"
                        sx={{ width: 56, height: 56 }}
                    />
                    <Typography variant="h6" fontWeight={700} textAlign="center">
                        ¿Dónde te entregamos?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        Selecciona tu dirección para ver tiendas y productos cercanos
                    </Typography>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
                    {!auth.isAuthenticated ? (
                        <>
                            <TextField
                                fullWidth
                                placeholder="Buscar dirección..."
                                value={addressInput}
                                onChange={(e) => setAddressInput(e.target.value)}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {searching
                                                    ? <CircularProgress size={18} sx={{ color: "#D4251C" }} />
                                                    : <SearchIcon sx={{ color: "text.secondary" }} />
                                                }
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        fontSize: 15,
                                    },
                                }}
                            />

                            {nodata && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    textAlign="center"
                                    sx={{ mt: 2 }}
                                >
                                    No se encontró la dirección. Intenta con otra búsqueda.
                                </Typography>
                            )}

                            {addressSuggestions.length > 0 && !searching && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ px: 0.5 }}>
                                        RESULTADOS
                                    </Typography>
                                    <List disablePadding sx={{ mt: 0.5 }}>
                                        {addressSuggestions.map((res) => {
                                            const location = res.geometry.location;
                                            return (
                                                <ListItemButton
                                                    key={res.place_id}
                                                    onClick={() => {
                                                        Cookies.set("user_coords", `${location.lng()},${location.lat()}`);
                                                        setAddressSuggestions([]);
                                                        window.location.reload();
                                                    }}
                                                    sx={{
                                                        borderRadius: 2,
                                                        py: 1.5,
                                                        "&:hover": { bgcolor: "grey.50" },
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                                        <LocationOnIcon sx={{ color: "#D4251C", fontSize: 20 }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={res.formatted_address}
                                                        slotProps={{ primary: { fontSize: 14, lineHeight: 1.4 } }}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                                </Box>
                            )}
                        </>
                    ) : (
                        <>
                            {currentAddress && (
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 1.5,
                                        bgcolor: "#fff5f5",
                                        borderRadius: 2,
                                        border: "1px solid #fecaca",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <LocationOnIcon sx={{ color: "#D4251C", fontSize: 18, flexShrink: 0 }} />
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        <strong>Actual:</strong> {currentAddress}
                                    </Typography>
                                </Box>
                            )}

                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                MIS DIRECCIONES
                            </Typography>

                            {addressList.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 3 }}>
                                    No tienes direcciones guardadas.
                                </Typography>
                            ) : (
                                <List disablePadding sx={{ mt: 1 }}>
                                    {addressList.map((item) => {
                                        const isSelected = selectedAddress === item.address;
                                        return (
                                            <Box key={item.id}>
                                                <ListItemButton
                                                    onClick={() => handleSelect(item.name, item.longitude, item.latitude)}
                                                    sx={{
                                                        borderRadius: 2,
                                                        py: 1.5,
                                                        bgcolor: isSelected ? "#fff5f5" : "transparent",
                                                        border: "1px solid",
                                                        borderColor: isSelected ? "#fecaca" : "transparent",
                                                        mb: 0.5,
                                                        "&:hover": {
                                                            bgcolor: isSelected ? "#fff0f0" : "grey.50",
                                                        },
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                                        <Box
                                                            sx={{
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                bgcolor: isSelected ? "#D4251C" : "grey.100",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            <LocationOnIcon
                                                                sx={{
                                                                    fontSize: 18,
                                                                    color: isSelected ? "white" : "grey.500",
                                                                }}
                                                            />
                                                        </Box>
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {item.name}
                                                                </Typography>
                                                                {isSelected && (
                                                                    <Chip
                                                                        label="Actual"
                                                                        size="small"
                                                                        sx={{
                                                                            height: 18,
                                                                            fontSize: 10,
                                                                            bgcolor: "#D4251C",
                                                                            color: "white",
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        }
                                                        secondary={item.address}
                                                        slotProps={{
                                                            secondary: { fontSize: 12, color: "text.secondary", noWrap: true },
                                                        }}
                                                    />
                                                    {isSelected && (
                                                        <CheckCircleIcon sx={{ color: "#D4251C", fontSize: 20, ml: 1 }} />
                                                    )}
                                                </ListItemButton>
                                            </Box>
                                        );
                                    })}
                                </List>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Modal>
    );
}
